import { FC, useMemo, useRef } from "react";
import { Box, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SimpleBarChart from "../../../../components/charts/graph/SimpleBarChart";
import DropdownSelect, { DropdownSelectObject } from "../../../../components/common/DropdownSelect";
import TableData from "../../../../components/common/table/TableData";
import PaperCard from "../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../components/widgets/PaperCard/PaperCardHeader";
import { LabeledValueDTO } from "../../../../datasources/api-client/data-contracts";
import { ThemeColors } from "../../../../theme/themeValues";
import { SourcesTable } from "../../ResultsOperationalUnits/tabs/Greenrouter";

interface Props {
	mode?: "select" | "tabs"; // Determines layout of selectors
	periodType?: "month" | "quarter" | "four-month" | "semester"; // Optional period dropdown
	setPeriodType?: (periodType: "month" | "quarter" | "four-month" | "semester") => void; // Setter for period
	onReload: VoidFunction; // Reload handler for the card
	isLoading: boolean; // Loading state
	selectedKey?: string; // Selected emission key (old dropdown)
	handleSelectKeyChange?: (value: string) => void; // Key change handler
	selectTitle: string; // Title above selectors
	chartData: LabeledValueDTO[]; // Data for chart
	chartMeasure: string; // Measure unit for chart
	menuOptions?: DropdownSelectObject[]; // Options for old key dropdown or tabs
	chartColor?: string | null; // Optional chart color
	sourceEmission?: SourcesTable; // Optional table for tabs mode
	consumptionReload?: VoidFunction; // Reload for table
	titleSourceEmission?: string; // Table title
	intervalMode?: "emissions" | "energy"; // Optional mode dropdown
	onIntervalModeChange?: (mode: "emissions" | "energy") => void; // Interval mode setter
}

const EmissionsIntervals: FC<Props> = ({
	onReload,
	isLoading,
	mode = "select",
	periodType,
	setPeriodType,
	menuOptions,
	selectedKey,
	handleSelectKeyChange,
	selectTitle,
	chartData,
	chartMeasure,
	chartColor,
	sourceEmission,
	consumptionReload,
	titleSourceEmission,
	intervalMode,
	onIntervalModeChange,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null); // Ref for card header
	const { t } = useTranslation(["feature.units", "common"]); // Translation hook

	// Compute current tab value if using tabs
	const currentTabValue = useMemo(() => {
		if (selectedKey && menuOptions && menuOptions.some((opt) => opt.key === selectedKey)) {
			return selectedKey;
		}
		// fallback to first tab key if nothing selected
		return menuOptions?.[0]?.key ?? "";
	}, [selectedKey, menuOptions]);

	// Options for interval mode dropdown
	const intervalModeOptions: DropdownSelectObject[] = [
		{ key: "emissions", label: t("common:emissions") + " tCO2e" },
		{ key: "energy", label: t("common:energy") + " GJ" },
	];

	// Dropdown for key selection (only used if intervalMode is not passed)
	const selectKeyEmission =
		mode === "select" && selectedKey && menuOptions ? (
			<DropdownSelect
				value={selectedKey}
				loading={isLoading}
				onChange={(value: string) => handleSelectKeyChange?.(value)}
				dropdownItems={menuOptions}
				variant="outlined"
			/>
		) : null;

	// Period dropdown options
	const periodDropdownItems: DropdownSelectObject[] = [
		{ key: "month", label: t("common:month") },
		{ key: "quarter", label: t("common:quarter") },
		{ key: "four-month", label: t("common:four-month") },
		{ key: "semester", label: t("common:semester") },
	];

	// Period dropdown component
	const selectPeriodType =
		(mode === "select" && periodType) || (sourceEmission && periodType) ? (
			<DropdownSelect
				value={periodType!}
				dropdownItems={periodDropdownItems}
				loading={isLoading}
				onChange={(value) => setPeriodType?.(value as "month" | "quarter" | "four-month" | "semester")}
				variant="outlined"
			/>
		) : null;

	// Tabs layout for key selection
	const tabSelector =
		mode === "tabs" ? (
			<Stack spacing={2} direction={"column"} width={"100%"} alignItems={"flex-start"}>
				<Tabs
					value={currentTabValue}
					onChange={(_, newValue) => handleSelectKeyChange && handleSelectKeyChange(newValue)}
					variant="scrollable"
					scrollButtons="auto"
				>
					{menuOptions?.map((opt) => (
						<Tab key={opt.key} label={opt.label} value={opt.key} />
					))}
				</Tabs>
				{selectPeriodType}
			</Stack>
		) : null;

	// Header with selectors
	const selectorHeader =
		mode === "select" ? (
			<Stack alignItems="center" direction="row" spacing={1.5}>
				<Typography variant="subtitle1">{selectTitle}</Typography>
				{intervalMode && onIntervalModeChange ? (
					// Show intervalMode dropdown if passed, hide old key dropdown
					<DropdownSelect
						value={intervalMode}
						dropdownItems={intervalModeOptions}
						onChange={(value) => onIntervalModeChange(value as "emissions" | "energy")}
						variant="outlined"
					/>
				) : (
					// Show old key dropdown if intervalMode is not passed
					selectKeyEmission
				)}
				{selectPeriodType}
			</Stack>
		) : (
			tabSelector // Use tabs if mode is "tabs"
		);

	if (isLoading) return <Skeleton variant="rounded" height="100%" width="100%" />;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					title={t("feature.units:overview/consumption trends and details")} // Card title
					containerRef={containerRef}
					element={selectorHeader} // Header with dropdowns or tabs
					isLoading={isLoading}
					onReload={onReload}
					sx={{ pb: 2 }}
				/>
			}
			content={
				<Stack direction="column" height="100%" width="100%" spacing={2}>
					<Stack minHeight={mode === "tabs" ? 280 : "100%"}>
						<SimpleBarChart
							data={chartData}
							color={chartColor || ThemeColors.LightSecondary}
							measure={chartMeasure}
							isTabMode={mode === "tabs"}
						/>
					</Stack>

					{mode === "tabs" && (
						<Box px={0} pt={2} pb={0}>
							{sourceEmission && consumptionReload && (
								<TableData
									title={titleSourceEmission ?? ""} // Optional table title
									columns={sourceEmission.columns}
									rows={sourceEmission.rows}
									isLoading={false}
									onReload={consumptionReload}
									elevation={0}
								/>
							)}
						</Box>
					)}
				</Stack>
			}
		/>
	);
};

export default EmissionsIntervals;
